import { useEffect, useRef, useState } from "react";
import { http, fmtNum } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranch } from "../context/BranchContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Banknote, Check, X, Clock, ArrowRight, PackageSearch } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 12000;

function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const notes = [523, 784, 1046];
    const dur = 0.16;
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(f, now + i * dur);
      g.gain.setValueAtTime(0.0001, now + i * dur);
      g.gain.exponentialRampToValueAtTime(0.3, now + i * dur + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * dur + dur - 0.02);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + i * dur);
      osc.stop(now + i * dur + dur);
    });
    setTimeout(() => { try { ctx.close(); } catch {} }, 900);
  } catch {}
}

const STATUS_META = {
  pending:   { label: "Menunggu", cls: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  approved:  { label: "Disetujui", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: Check },
  rejected:  { label: "Ditolak",   cls: "bg-rose-100 text-rose-800 border-rose-300", icon: X },
  cancelled: { label: "Dibatalkan", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: X },
};

/**
 * Bell notifikasi Permintaan Valas — dual-role:
 *  • HQ (Pusat) admin/super_admin: melihat permintaan masuk untuk approve/reject.
 *  • Cabang (non-HQ) semua user: melihat status permintaannya (disetujui/ditolak).
 * Real-time polling 12 s + chime + toast pada perubahan.
 */
export default function ValasRequestBell({ onCount } = {}) {
  const { user } = useAuth();
  const { currentBranch, branches } = useBranch();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const hq = (branches || []).find((b) =>
    ["PUSAT", "KP", "MC1", "MC 1"].includes((b.code || "").toUpperCase()) ||
    /(MC 1|Pusat)/i.test(b.name || "")
  );
  const isHqUser = isAdmin && (!currentBranch || !hq || currentBranch.id === hq.id);
  const scope = isHqUser ? "hq" : "mine";

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [approveFor, setApproveFor] = useState(null);
  const [approveNote, setApproveNote] = useState("");
  const [busy, setBusy] = useState(false);
  const knownStatus = useRef({}); // id → status snapshot
  const bootstrapped = useRef(false);

  const fetchList = async () => {
    try {
      const { data } = await http.get(`/valas-requests?scope=${scope}&limit=30`);
      const arr = (data && Array.isArray(data.items)) ? data.items : [];
      if (bootstrapped.current) {
        // Detect state changes
        for (const r of arr) {
          const prev = knownStatus.current[r.id];
          if (!prev) {
            // NEW request appeared
            if (isHqUser && r.status === "pending") {
              playChime();
              toast.warning(`Permintaan valas baru — ${r.from_branch_name} minta ${fmtNum(r.amount, 2)} ${r.code}`, {
                description: r.customer_name ? `Nasabah: ${r.customer_name}` : "Perlu review",
                duration: 8000,
              });
            }
          } else if (prev !== r.status && !isHqUser) {
            // My own request was resolved
            if (r.status === "approved") {
              playChime();
              toast.success(`Permintaan valas Anda DISETUJUI — ${fmtNum(r.approved_amount || r.amount, 2)} ${r.code}`, {
                description: r.resolved_note || "Silakan konfirmasi ke Pusat",
                duration: 8000,
              });
            } else if (r.status === "rejected") {
              toast.error(`Permintaan valas ${fmtNum(r.amount, 2)} ${r.code} DITOLAK`, {
                description: r.resolved_note || "Hubungi Pusat untuk info",
                duration: 8000,
              });
            }
          }
        }
      }
      // Snapshot
      knownStatus.current = Object.fromEntries(arr.map((r) => [r.id, r.status]));
      bootstrapped.current = true;
      setItems(arr);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    fetchList();
    const t = setInterval(fetchList, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isHqUser]);

  const pendingCount = items.filter((r) => r.status === "pending").length;
  useEffect(() => { onCount?.(pendingCount); }, [pendingCount]);

  const approve = async () => {
    if (!approveFor) return;
    setBusy(true);
    try {
      await http.post(`/valas-requests/${approveFor.id}/approve`, { note: approveNote });
      toast.success(`Permintaan ${approveFor.from_branch_name} disetujui. Transfer otomatis dibuat.`);
      setApproveFor(null); setApproveNote("");
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal approve");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectFor) return;
    if (!rejectNote.trim()) return toast.error("Alasan penolakan wajib diisi");
    setBusy(true);
    try {
      await http.post(`/valas-requests/${rejectFor.id}/reject`, { note: rejectNote });
      toast.success(`Permintaan ${rejectFor.from_branch_name} ditolak`);
      setRejectFor(null); setRejectNote("");
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal reject");
    } finally { setBusy(false); }
  };

  const cancel = async (r) => {
    if (!window.confirm("Batalkan permintaan valas ini?")) return;
    try {
      await http.post(`/valas-requests/${r.id}/cancel`);
      toast.success("Permintaan dibatalkan");
      fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  if (!user) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white/60 text-slate-600 hover:bg-slate-100 transition"
        data-testid="valas-request-bell"
        title={isHqUser ? "Permintaan valas dari cabang" : "Status permintaan valas Anda"}
      >
        <Banknote size={18} className={pendingCount > 0 ? "text-sky-600" : "text-slate-500"} />
        {pendingCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-sky-600 text-white text-[9px] font-bold flex items-center justify-center animate-pulse">
            {pendingCount}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageSearch size={16} />
              {isHqUser ? "Permintaan Valas dari Cabang" : "Status Permintaan Valas Saya"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2" data-testid="valas-request-list">
            {items.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">Belum ada permintaan valas.</div>
            )}
            {items.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              const Icon = meta.icon;
              return (
                <div key={r.id} className={`border rounded-md p-3 ${r.status === "pending" && isHqUser ? "border-sky-300 bg-sky-50/40" : "border-slate-200"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                        <span>{r.from_branch_name}</span>
                        <ArrowRight size={12} className="text-slate-400" />
                        <span>{r.to_branch_name}</span>
                        {r.urgent && <span className="ml-1 px-1.5 py-0.5 rounded bg-rose-600 text-white text-[9px] font-bold">URGENT</span>}
                      </div>
                      <div className="mt-1 text-lg font-bold font-mono-num text-slate-900">
                        {fmtNum(r.amount, 2)} {r.code}
                        {r.approved_amount != null && r.approved_amount !== r.amount && (
                          <span className="ml-2 text-xs text-emerald-700">(disetujui {fmtNum(r.approved_amount, 2)})</span>
                        )}
                      </div>
                      {r.customer_name && (
                        <div className="text-[11px] text-slate-500 mt-0.5">Nasabah: <b>{r.customer_name}</b></div>
                      )}
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {new Date(r.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} · oleh {r.requested_by_name}
                      </div>
                      {r.note && <div className="text-[11px] italic text-slate-600 mt-1">"{r.note}"</div>}
                      {r.resolved_note && (
                        <div className="text-[11px] italic text-slate-700 mt-1">
                          Catatan {r.status === "approved" ? "Pusat" : "penolakan"}: "{r.resolved_note}"
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-semibold ${meta.cls}`}>
                        <Icon size={11} /> {meta.label}
                      </span>
                      {r.status === "pending" && isHqUser && (
                        <div className="flex flex-col gap-1 mt-2">
                          <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => { setApproveFor(r); setApproveNote(""); }}
                            data-testid={`btn-approve-valas-${r.id}`}>
                            <Check size={11} className="mr-1" /> Setujui
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                            onClick={() => { setRejectFor(r); setRejectNote(""); }}
                            data-testid={`btn-reject-valas-${r.id}`}>
                            <X size={11} className="mr-1" /> Tolak
                          </Button>
                        </div>
                      )}
                      {r.status === "pending" && !isHqUser && r.requested_by_id === user.id && (
                        <Button size="sm" variant="outline" className="mt-2 h-7 text-[11px] border-slate-300"
                          onClick={() => cancel(r)}
                          data-testid={`btn-cancel-valas-${r.id}`}>
                          Batalkan
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={!!approveFor} onOpenChange={(v) => !v && setApproveFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Setujui Permintaan Valas</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="rounded-md border bg-emerald-50/50 p-2 text-xs">
              <div><b>{approveFor?.from_branch_name}</b> minta <b>{approveFor && fmtNum(approveFor.amount, 2)} {approveFor?.code}</b></div>
              {approveFor?.customer_name && <div className="text-slate-500 mt-0.5">Nasabah: {approveFor.customer_name}</div>}
            </div>
            <Textarea rows={2} placeholder="Catatan (opsional)…" value={approveNote} onChange={(e) => setApproveNote(e.target.value)} />
            <div className="text-[11px] text-slate-500 italic">Menyetujui otomatis membuat transfer valas dari Pusat ke cabang pengaju.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveFor(null)}>Batal</Button>
            <Button onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-submit-approve-valas">
              {busy ? "Memproses…" : "Setujui & Kirim"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Tolak Permintaan Valas</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="rounded-md border bg-rose-50/50 p-2 text-xs">
              <div><b>{rejectFor?.from_branch_name}</b> minta <b>{rejectFor && fmtNum(rejectFor.amount, 2)} {rejectFor?.code}</b></div>
            </div>
            <Textarea rows={2} placeholder="Alasan penolakan (wajib)…" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} data-testid="valas-reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Batal</Button>
            <Button onClick={reject} disabled={busy || !rejectNote.trim()} variant="destructive" data-testid="btn-submit-reject-valas">
              {busy ? "Memproses…" : "Tolak Permintaan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
